import classNames from 'classnames';
import { Colors, ColorsType, GradientsType } from '@vds/core';
import { compact } from '@vds/core';
import './GridItem.scss';

type GridSpanValue =
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10
  | 11
  | 12
  | 13
  | 14
  | 15
  | 16
  | 17
  | 18
  | 19
  | 20
  | 21
  | 22
  | 23
  | 24;

export interface GridItemResponsiveSpan {
  mobilePortrait?: GridSpanValue;
  mobileLandscape?: GridSpanValue;
  tabletPortrait?: GridSpanValue;
  tabletLandscape?: GridSpanValue;
  desktop?: GridSpanValue;
  desktopWide?: GridSpanValue;
}

export interface GridItemCssProps {
  gridArea?: React.CSSProperties['gridArea'];
  gridColumn?: React.CSSProperties['gridColumn'];
  gridRow?: React.CSSProperties['gridRow'];
  gridColumnStart?: React.CSSProperties['gridColumnStart'];
  gridColumnEnd?: React.CSSProperties['gridColumnEnd'];
  gridRowStart?: React.CSSProperties['gridRowStart'];
  gridRowEnd?: React.CSSProperties['gridRowEnd'];
  justifyContent?: React.CSSProperties['justifyContent'];
  height?: React.CSSProperties['height'];
  width?: React.CSSProperties['width'];
}
export interface GridItemProps extends GridItemCssProps {
  bg?: ColorsType | GradientsType;
  aspectRatio?:
    | 'auto'
    | '1 / 1'
    | '2 / 3'
    | '3 / 4'
    | '4 / 5'
    | '9 / 16'
    | '9 / 21'
    | '16 / 10'
    | '21 / 9'
    | '16 / 9'
    | '5 / 4'
    | '4 / 3';
  variant?: 'portrait' | 'landscape';
  responsiveSpan?: GridItemResponsiveSpan;
  children?: React.ReactNode;
  className?: string;
}

/**
 * GridItem component
 * @param gridArea - {@link https://developer.mozilla.org/en-US/docs/Web/CSS/grid-area | gridArea} css property.
 * @param gridColumn - {@link https://developer.mozilla.org/en-US/docs/Web/CSS/grid-column | gridColumn} css property.
 * @param gridRow - {@link https://developer.mozilla.org/en-US/docs/Web/CSS/grid-row | gridRow} css property.
 * @param gridColumnStart - {@link https://developer.mozilla.org/en-US/docs/Web/CSS/grid-column-start | gridColumnStart} css property.
 * @param gridColumnEnd - {@link https://developer.mozilla.org/en-US/docs/Web/CSS/grid-column-end | gridColumnEnd} css property.
 * @param gridRowStart - {@link https://developer.mozilla.org/en-US/docs/Web/CSS/grid-row-start | gridRowStart} css property.
 * @param gridRowEnd - {@link https://developer.mozilla.org/en-US/docs/Web/CSS/grid-row-end | gridRowEnd} css property.
 * @param justifyContent - {@link https://developer.mozilla.org/en-US/docs/Web/CSS/justify-content | justifyContent} css property.
 * @param height - {@link https://developer.mozilla.org/en-US/docs/Web/CSS/height | height} css property.
 * @param width - {@link https://developer.mozilla.org/en-US/docs/Web/CSS/width | width} css property.
 * @param bg - Background color for the item.
 * @param aspectRatio - Aspect ratio for the item.
 * @param variant - Variant for the component choose between portrait or landscape grid item.
 * @param responsiveSpan - Override the default grid-column span at specific breakpoints.
 * @param children - Any React.ReactNode element.
 * @param className - Additional classNames for the component.
 */
export const GridItem: React.FC<GridItemProps> = ({
  bg = Colors.Other.Transparent,
  aspectRatio,
  variant = 'portrait',
  responsiveSpan,
  children,
  gridArea,
  gridColumn,
  gridRow,
  gridColumnStart,
  gridColumnEnd,
  gridRowStart,
  gridRowEnd,
  justifyContent,
  height,
  width,
  className,
  ...rest
}) => {
  const spanVars = responsiveSpan
    ? compact({
        '--vds-grid-item-span-mp': responsiveSpan.mobilePortrait,
        '--vds-grid-item-span-ml': responsiveSpan.mobileLandscape,
        '--vds-grid-item-span-tp': responsiveSpan.tabletPortrait,
        '--vds-grid-item-span-tl': responsiveSpan.tabletLandscape,
        '--vds-grid-item-span-d': responsiveSpan.desktop,
        '--vds-grid-item-span-dw': responsiveSpan.desktopWide,
      })
    : {};

  const style = compact({
    ...spanVars,
    background: `var(${bg})`,
    aspectRatio,
    gridArea,
    gridColumn,
    gridRow,
    gridColumnStart,
    gridColumnEnd,
    gridRowStart,
    gridRowEnd,
    justifyContent,
    height,
    width,
  }) as React.CSSProperties;

  const hasResponsiveSpan = Object.keys(spanVars).length > 0;

  return (
    <div
      className={classNames(
        'vds-grid__item',
        [`vds-grid__item--${variant}`],
        hasResponsiveSpan && 'vds-grid__item--responsive-span',
        className,
      )}
      style={style}
      {...rest}>
      {children}
    </div>
  );
};
